import { lazy, Suspense } from 'react';
import { Alert, Center, ScrollArea, Space, Stack } from '@mantine/core';
import {
  isCsv,
  isDirectory,
  isGif,
  isHtml,
  isImage,
  isJson,
  isMarkdown,
  isMicrosoftOfficeFile,
  isPdf,
  isText,
  isVideo,
} from '@/utils/datasetTypes.ts';
import { Dataset } from '@/types/datasets.ts';
import { IconInfoCircle } from '@tabler/icons-react';
import CustomSpinner from '@/components/Spinner.tsx';
import LazyLoadErrorBoundary from '@/components/LazyLoadErrorBoundary.tsx';

const TextViewer = lazy(() => import('@/layouts/DataViewer/Viewers/TextViewer.tsx'));
const DirectoryViewer = lazy(() => import('@/layouts/DataViewer/Viewers/DirectoryViewer.tsx'));
const CsvViewer = lazy(() => import('@/layouts/DataViewer/Viewers/CsvViewer.tsx'));
const JsonViewer = lazy(() => import('@/layouts/DataViewer/Viewers/JsonViewer.tsx'));
const OfficeViewer = lazy(() => import('@/layouts/DataViewer/Viewers/OfficeViewer.tsx'));
const GifViewer = lazy(() => import('@/layouts/DataViewer/Viewers/GifViewer/GifViewer.tsx'));
const ImageViewer = lazy(() => import('@/layouts/DataViewer/Viewers/ImageViewer.tsx'));
const PdfViewer = lazy(() => import('@/layouts/DataViewer/Viewers/PdfViewer.tsx'));
const HtmlViewer = lazy(() => import('@/layouts/DataViewer/Viewers/HtmlViewer.tsx'));
const MarkdownViewer = lazy(() => import('@/layouts/DataViewer/Viewers/MarkdownViewer.tsx'));
const VideoViewer = lazy(() => import('@/layouts/DataViewer/Viewers/VideoViewer.tsx'));

type props = {
  dataset: Dataset;
  datasetURL: string;
  fileName: string;
  blob_exists: boolean;
};

const DataViewer = (props: props) => {
  return (
    <Stack
      flex={1}
      w="100%"
      style={{ minHeight: 0, background: '#F1F1F1' }}
      px={'md'}
      pt={'md'}
      gap={'sm'}
      h={'100%'}
    >
      <LazyLoadErrorBoundary resetKey={props.dataset.id}>
        <Suspense fallback={<CustomSpinner />}>
          <DataViewerInner {...props} />
        </Suspense>
      </LazyLoadErrorBoundary>
      <Space h="1px" w={'100%'} />
    </Stack>
  );
};

const DataViewerInner = ({ dataset, datasetURL, fileName, blob_exists }: props) => {
  if (isDirectory(dataset)) {
    return <DirectoryViewer dataset={dataset} />;
  }

  if (!blob_exists) {
    return (
      <Center h={'100%'} w={'100%'}>
        <Alert
          color="orange"
          radius="md"
          title="Dataset Unavailable"
          autoContrast={true}
          icon={<IconInfoCircle />}
        >
          Dataset file is not available. It may have been deleted or an error/interruption occurred.
          Please contact support for assistance.
        </Alert>
      </Center>
    );
  }

  if (isPdf(dataset)) {
    return <PdfViewer key={dataset.id} url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (isGif(dataset)) {
    return <GifViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (isVideo(dataset)) {
    return <VideoViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (isImage(dataset)) {
    return <ImageViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  // if (isExtractStepOutput(dataset)) {
  //   return <JsonViewer url={datasetURL} dataset={dataset} extractView={true} />;
  // }

  if (isJson(dataset)) {
    return <JsonViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (isCsv(dataset)) {
    return <CsvViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (isHtml(dataset)) {
    return <HtmlViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (isMarkdown(dataset)) {
    return <MarkdownViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (isText(dataset)) {
    return <TextViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  if (dataset.has_original_blob && isMicrosoftOfficeFile(dataset)) {
    return <OfficeViewer url={datasetURL} fileName={fileName} dataset={dataset} />;
  }

  return (
    <ScrollArea w="100%" h="100%" styles={{ content: { height: '100%' } }}>
      <iframe
        src={datasetURL}
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="Dataset Viewer"
      />
    </ScrollArea>
  );
};

export default DataViewer;
